import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import Dropdown, { StyledDropdownIcon } from '@components/Dropdown';
import { Notifications as NotificationsIcon } from '@icons/crm';
import Notifications from '@components/CRM/Notifications/Notifications';
import { Throbber } from '@components';
import { INotification } from 'types/types';

interface NotificationsDropdownProps {
  className?: string;
  notifications: INotification[];
  isLoading: boolean;
  unreadCount: number;
  fetchNotifications: () => void;
}

const NotificationsDropdown = ({
  className,
  notifications,
  isLoading,
  unreadCount,
  fetchNotifications,
}: NotificationsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!notifications || !notifications.length) {
      fetchNotifications();
    }
  }, []);

  const headRenderer = useMemo(
    () => (
      <StyledHead>
        <StyledSvgWrapper $hasUnreadNotifications={!!unreadCount}>
          <NotificationsIcon width={20} />
        </StyledSvgWrapper>
        <StyledLabel>
          {isLoading && !notifications.length ? (
            <Throbber size={10} color="#262729" />
          ) : (
            unreadCount
          )}
        </StyledLabel>
      </StyledHead>
    ),
    [notifications, isLoading, unreadCount]
  );

  const contentRenderer = useMemo(() => {
    return (
      <StyledContent>
        <Notifications setIsOpen={setIsOpen} />
      </StyledContent>
    );
  }, [notifications]);

  const toggleDropdown = isOpen => {
    setIsOpen(isOpen);
  };

  return (
    <StyledNotificationsDropdown
      className={className}
      headRenderer={headRenderer}
      contentRenderer={contentRenderer}
      onToggle={toggleDropdown}
      open={isOpen}
    />
  );
};

const StyledSvgWrapper = styled.div<{ $hasUnreadNotifications: boolean }>`
  ${({ $hasUnreadNotifications }) =>
    $hasUnreadNotifications &&
    css`
      &::before {
        content: '';
        position: absolute;
        width: 8px;
        height: 8px;
        display: block;
        background: red;
        top: 0;
        right: 0;
        border-radius: 50%;
      }
    `}
`;

const StyledLabel = styled.span`
  position: relative;
  z-index: 3;
`;

const StyledNotificationsDropdown = styled(Dropdown)`
  background: white;
  font-size: 13px;
  position: relative;
  border: none;
  border-radius: 4px;
  ${StyledDropdownIcon} {
    display: none;
  }
`;

const StyledHead = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
  display: flex;
  column-gap: 5px;
  height: 40px;
  padding: 0 7px;
  border: solid 1px #c2c5cc;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  > * {
    position: relative;
    z-index: 3;
  }
`;

const StyledContent = styled.div`
  width: 500px;
`;

export default NotificationsDropdown;
